// src/services/config/firebase.js - Production Ready Configuration
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getRemoteConfig } from 'firebase/remote-config';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Your Firebase config object - REPLACE WITH YOUR ACTUAL CONFIG
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId'];
  const missingFields = requiredFields.filter(field => 
    !firebaseConfig[field] || firebaseConfig[field].includes('your-'),
  );
  
  if (missingFields.length > 0) {
    console.warn('⚠️ Firebase config incomplete. Missing or placeholder values for:', missingFields);
    console.warn('📝 Please update src/services/config/firebase.js with your actual Firebase project credentials.');
    return false;
  }
  
  return true;
};

// Initialize Firebase only if config is valid
let app = null;
let auth = null;
let db = null;
let analytics = null;
let performance = null;
let remoteConfig = null;

try {
  if (validateFirebaseConfig()) {
    console.log('🔥 Initializing Firebase...');
    
    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    
    // Initialize Auth
    auth = getAuth(app);
    
    // Initialize Firestore
    db = getFirestore(app);
    
    // Initialize Analytics
    if (firebaseConfig.measurementId) {
      try {
        analytics = getAnalytics(app);
        console.log('📊 Firebase Analytics initialized');
      } catch (analyticsError) {
        console.warn('⚠️ Analytics initialization failed:', analyticsError.message);
      }
    }
    
    // Initialize Performance (only in production)
    if (import.meta.env.MODE === 'production') {
      try {
        performance = getPerformance(app);
        console.log('⚡ Firebase Performance monitoring initialized');
      } catch (perfError) {
        console.warn('⚠️ Performance monitoring initialization failed:', perfError.message);
      }
    }
    
    // Initialize Remote Config
    if (import.meta.env.MODE === 'production') {
      try {
        remoteConfig = getRemoteConfig(app);
        console.log('🔧 Firebase Remote Config initialized');
      } catch (remoteConfigError) {
        console.warn('⚠️ Remote Config initialization failed:', remoteConfigError.message);
      }
    }
    
    // Connect to emulators in development (optional)
    if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
      console.log('🔧 Connecting to Firebase emulators...');
      
      try {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        connectFirestoreEmulator(db, 'localhost', 8080);
        console.log('✅ Connected to Firebase emulators');
      } catch (emulatorError) {
        console.warn('⚠️ Emulator connection failed:', emulatorError.message);
      }
    }
    
    console.log('✅ Firebase initialized successfully');
  } else {
    console.log('⚠️ Firebase initialization skipped due to incomplete configuration');
  }
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);

  // The same outcome as having no config at all: null handles, which every
  // caller reads as "no Firebase here" (`auth?.currentUser`, `if (!db)`).
  //
  // Stand-in objects used to be built here instead. Nothing could use them:
  // they had the old namespaced shape (`db.collection().doc()`), which the
  // modular functions the app calls (`doc(db, ...)`) reject exactly as they
  // reject null, and being truthy they slipped past the `!db` checks. The
  // stand-in auth's onAuthStateChanged returned nothing, so the listener's
  // clean-up threw "unsubscribe is not a function".
  auth = null;
  db = null;
}

/**
 * Be told who is signed in to this project, now and whenever that changes.
 * Returns an unsubscribe function.
 *
 * Subscribe through this, not by handing `auth` to the SDK's own
 * onAuthStateChanged. `auth` is null when the build has no Firebase config (a
 * fresh checkout has only .env.example) or initialisation failed, and the SDK
 * function throws on a null handle - which took the whole app down at boot
 * with "Cannot read properties of null (reading 'onAuthStateChanged')".
 *
 * With no Firebase nobody can be signed in through it, so the listener hears
 * `null` once and the app carries on local-only.
 */
export const subscribeToAuthState = (callback) => {
  if (auth) return onAuthStateChanged(auth, callback);

  // Later, not straight away - the SDK never calls back before it has
  // returned, and a listener is entitled to rely on that. Unsubscribing
  // first means it is never called, as with the SDK.
  let subscribed = true;
  Promise.resolve().then(() => {
    if (subscribed) callback(null);
  });
  return () => {
    subscribed = false;
  };
};

// Helper function to check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  return app !== null && validateFirebaseConfig();
};

// Helper function to get connection status
export const getFirebaseStatus = () => {
  return {
    configured: isFirebaseConfigured(),
    hasAuth: !!auth,
    hasFirestore: !!db,
    hasAnalytics: !!analytics,
    hasPerformance: !!performance,
    environment: import.meta.env.MODE,
  };
};

// ---------------------------------------------------------------------------
// Auth hub — a SECOND, NAMED Firebase app.
//
// The hub's getAppState/saveAppState derive identity from the caller's own
// session, and they only accept a HUB-audience token. Signing into this
// project with the hub-minted spoke token gives a spoke-audience token, which
// the hub cannot verify - the symptom is a bare "Unauthenticated" from
// getAppState. So the hub gets its own app instance and its own session.
//
// It MUST be a named app ('auth-hub'). Sharing the default app would make the
// hub session look like this project's session, and every visitor would
// appear signed in.
// ---------------------------------------------------------------------------
const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: 'ddd-auth-hub-4ed14',
};

let authHubApp = null;
let authHubAuth = null;

if (authHubConfig.apiKey) {
  try {
    authHubApp = initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = getAuth(authHubApp);
  } catch (error) {
    console.error('Auth hub initialisation error:', error);
  }
}

export { authHubApp, authHubAuth };

// Give the toolkit's local storage a way to resolve the signed-in uid, so
// each account's tool data is scoped to them rather than shared with whoever
// next opens the app on this device.
import { registerToolStorageAuth } from '../../utils/toolStorage';
registerToolStorageAuth(auth);

// Export Firebase services
export { app, auth, db, analytics, performance, remoteConfig };

// Export default auth for backward compatibility
export default auth;